import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Briefcase, FileText, MessageSquare, Settings, LogOut,
  CreditCard, ShieldCheck, Search, Users, Globe, Sliders,
  BookOpen, Megaphone, LifeBuoy, Inbox, MessageSquarePlus, Target, Shapes,
} from "lucide-react";
import Logo from "@/components/brand/Logo";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/notifications/NotificationBell";
import LanguageToggle from "@/components/layout/LanguageToggle";
import GuideModal from "@/components/guides/GuideModal";
import MobileSectionHeader from "@/components/nav/MobileSectionHeader";
import { sectionTitleKey } from "@/components/nav/panelSections";

/** The two Panel sections that are also cells of the bottom bar. */
const TAB_ROUTES = ["/provider/quotes", "/client/jobs"];

// The mobile top bar's one height (PENDING #2): the bar, the content's offset
// (`.dash-main` in index.css) and the admin drawer's anchor all read it.
const TOPBAR_HEIGHT = "var(--topbar-height)";

export default function DashboardLayout({ children, role = "client" }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [directedPending, setDirectedPending] = useState(0);

  /**
   * How many requests were sent to this FixyBee by name and are still waiting
   * for an answer (R6).
   *
   * The one fetch this layout does, and only for a provider. `limit=1` because
   * the rows are not wanted here — the count is its own query on the server
   * precisely so a badge can ask for it without paying for a page of results.
   * Polled on the bell's rhythm, and re-asked whenever the list page says the
   * answer may have moved.
   */
  useEffect(() => {
    if (role !== "provider") return undefined;
    let live = true;
    const load = () => api.get("/jobs?directed=true&limit=1")
      .then((r) => { if (live) setDirectedPending(r.data.pending || 0); })
      .catch(() => {});
    load();
    const id = setInterval(load, 60000);
    window.addEventListener("fixy:directed-changed", load);
    return () => {
      live = false;
      clearInterval(id);
      window.removeEventListener("fixy:directed-changed", load);
    };
  }, [role]);

  const clientNav = [
    { to: "/client/dashboard", label: t("dashLayout.overview"), icon: LayoutDashboard, key: "overview" },
    { to: "/client/jobs", label: t("dashLayout.myJobs"), icon: Briefcase, key: "my-jobs" },
    { to: "/client/messages", label: t("dashLayout.messages"), icon: MessageSquare, key: "messages" },
    { action: "guide", label: t("dashLayout.userGuide"), icon: BookOpen, key: "user-guide" },
    { to: "/client/settings", label: t("dashLayout.accountSettings"), icon: Settings, key: "account-settings" },
    // T15. The desktop counterpart of the Menú sheet's row. An action and not
    // a route, like the guide above it: it opens the survey where it stands.
    { action: "feedback", label: t("feedback.menuEntry"), icon: MessageSquarePlus, key: "send-feedback" },
  ];

  const providerNav = [
    { to: "/provider/dashboard", label: t("dashLayout.overview"), icon: LayoutDashboard, key: "overview" },
    { to: "/provider/browse", label: t("dashLayout.browseJobs"), icon: Search, key: "browse-jobs" },
    // R6. The house of the directed requests; the badge on /provider/browse is
    // the reminder. A count rides on this one because the whole finding was
    // that nothing on screen said a request was waiting once the push was gone.
    { to: "/provider/requests-for-you", label: t("dashLayout.requestsForYou"), icon: Inbox, key: "requests-for-you", badge: directedPending },
    { to: "/provider/quotes", label: t("dashLayout.myQuotes"), icon: FileText, key: "my-quotes" },
    // DY2.6. Always shown, not conditional on having posted anything: it is
    // also how a FixyBee finds out they can. A count would need a fetch this
    // layout does not do.
    { to: "/provider/requests", label: t("dashLayout.myRequests"), icon: Briefcase, key: "my-requests" },
    { to: "/provider/messages", label: t("dashLayout.messages"), icon: MessageSquare, key: "messages" },
    { to: "/provider/billing", label: t("dashLayout.billing"), icon: CreditCard, key: "billing" },
    // No FixyTerminal entry: out of scope, on no surface (PLANES, 2026-09-30).
    { to: "/provider/verification", label: t("dashLayout.verification"), icon: ShieldCheck, key: "verification" },
    { to: "/provider/landing-editor", label: t("dashLayout.webPageEditor"), icon: Globe, key: "web-page-editor" },
    { action: "guide", label: t("dashLayout.userGuide"), icon: BookOpen, key: "user-guide" },
    { to: "/provider/settings", label: t("dashLayout.accountSettings"), icon: Settings, key: "account-settings" },
    // T15. The desktop counterpart of the Menú sheet's row. An action and not
    // a route, like the guide above it: it opens the survey where it stands.
    { action: "feedback", label: t("feedback.menuEntry"), icon: MessageSquarePlus, key: "send-feedback" },
  ];

  const adminNav = [
    { to: "/admin", label: t("dashLayout.dashboardAdmin"), icon: LayoutDashboard, key: "dashboard" },
    { to: "/admin/users", label: t("dashLayout.users"), icon: Users, key: "users" },
    { to: "/admin/jobs", label: t("dashLayout.jobs"), icon: Briefcase, key: "jobs" },
    { to: "/admin/verifications", label: t("dashLayout.verifications"), icon: ShieldCheck, key: "verifications" },
    { to: "/admin/broadcasts", label: t("dashLayout.broadcasts"), icon: Megaphone, key: "broadcasts" },
    { to: "/admin/tickets", label: t("dashLayout.tickets"), icon: LifeBuoy, key: "tickets" },
    // T15. The record, next to the queue. An admin has no Menú sheet and no
    // survey of their own to send — this is the reading end, not the writing
    // end, which is why adminNav gets a ROUTE here and the other two navs get
    // the `feedback` action.
    { to: "/admin/feedback", label: t("feedback.adminTitle"), icon: MessageSquarePlus, key: "feedback" },
    { to: "/admin/acquisition", label: t("adminAcquisition.title"), icon: Target, key: "acquisition" },
    { to: "/admin/categories", label: t("adminCategories.title"), icon: Shapes, key: "categories" },
    { to: "/admin/config", label: t("dashLayout.config"), icon: Sliders, key: "config" },
    { action: "guide", label: t("dashLayout.adminGuide"), icon: BookOpen, key: "admin-guide" },
  ];

  const nav = role === "provider" ? providerNav : role === "admin" ? adminNav : clientNav;
  const isAdmin = role === "admin";

  /**
   * Below 992px every one of these pages is a SECTION of the Panel (T12-M4a),
   * and wears the spec's 56px header: "‹ Panel" and the section's title. The
   * minimum index M1 put on the overview is gone — `pages/Panel.jsx` is the
   * real index now, at its own address, as the spec draws it.
   *
   * Admin is excluded for the same reason it keeps its drawer: its eight
   * sections are not in the Panel.
   */
  const sectionKey = isAdmin ? null : sectionTitleKey(location.pathname);
  // A page that is also a tab only wears the back header when the Panel sent
  // us here; otherwise it is a top-level destination and the bar marks it.
  const fromPanel = !!location.state?.fromPanel;
  const alsoATab = TAB_ROUTES.includes(location.pathname);
  const showSectionHeader = !!sectionKey && (fromPanel || !alsoATab);

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    /* `app-shell`, not `min-h-screen`: the floor of this box has to be the
       viewport that is on screen, not the one the phone would have with its
       toolbar hidden. index.css says what that cost. */
    <div className="app-shell flex bg-cream" data-testid={`${role}-dashboard-layout`}>
      {/* Sidebar */}
      <aside className="hidden nav:flex w-64 flex-col bg-worker text-cream sticky top-0 h-screen">
        <div className="p-4 border-b border-cream/10">
          <div className="bg-cream rounded-md inline-block px-2 py-1.5">
            <Logo size={28} />
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((item) => {
            const Icon = item.icon;
            const cls = "flex items-center gap-3 px-3 py-2.5 rounded-sm font-display text-sm transition-colors w-full text-left";
            if (item.action) {
              const run = item.action === "feedback"
                ? () => window.dispatchEvent(new CustomEvent("fixy:open-feedback"))
                : () => setGuideOpen(true);
              return (
                <button
                  key={item.key}
                  onClick={run}
                  data-testid={`sidebar-${item.key}`}
                  className={`${cls} text-cream/80 hover:bg-cream/5 hover:text-cream`}
                >
                  <Icon size={18} /> {item.label}
                </button>
              );
            }
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`sidebar-${item.key}`}
                className={`${cls} ${
                  active ? "bg-hive text-worker font-semibold" : "text-cream/80 hover:bg-cream/5 hover:text-cream"
                }`}
              >
                <Icon size={18} /> <span className="flex-1 truncate">{item.label}</span>
                {/* On the active row the pill cannot be hive on hive, or the
                    count disappears exactly when you are looking at it. */}
                {item.badge > 0 && (
                  <span
                    className={`ml-auto min-w-5 px-1.5 h-5 rounded-full text-xs font-display font-bold flex items-center justify-center ${
                      active ? "bg-worker text-hive" : "bg-hive text-worker"
                    }`}
                    data-testid={`sidebar-${item.key}-badge`}
                  >
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-cream/10">
          <div className="px-3 py-2 mb-2">
            <p className="font-display font-semibold text-sm truncate">{user?.profile?.full_name || user?.email}</p>
            <p className="text-xs text-cream/60 truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-sm font-display text-sm text-cream/80 hover:bg-cream/5"
            data-testid="logout-btn"
          >
            <LogOut size={18} /> {t("dashLayout.logout")}
          </button>
        </div>
      </aside>

      {/* Mobile top bar — ADMIN ONLY, below 992px (T12-M1).
          For a client or a FixyBee this black bar and its drawer are gone: the
          bottom tab bar is the navigation and the language toggle is a row in
          its Menú sheet. What is left above is the mark, on the app's own
          canvas — and, since T14-M1, the bell beside it.
          Admin keeps the drawer because the tab bar does not run there — it is
          an internal console with eight sections and no place in a five-cell
          bar, the same reason `lib/danyPageMode` already keeps Dany off it.

          T12-M1 sent the bell's count to the Messages tab and left the bell
          itself with nowhere to be below 992px. T14 separates the two: the tab
          counts unread MESSAGES (M2) and the bell counts notifications, here,
          where it was always the only thing that showed them. */}
      {isAdmin ? (
        <div className="nav:hidden fixed top-0 left-0 right-0 z-30 bg-worker text-cream flex items-center justify-between px-3" style={{ height: TOPBAR_HEIGHT }} data-testid="mobile-topbar">
          <div className="bg-cream rounded-md inline-block px-2 py-1">
            <Logo size={24} />
          </div>
          <div className="flex items-center gap-1">
            <LanguageToggle dark />
            <NotificationBell />
            <button onClick={() => setMobileNav((v) => !v)} className="px-3 py-1.5 text-sm font-display" data-testid="mobile-dash-toggle">
              {t("dashLayout.menu")}
            </button>
          </div>
        </div>
      ) : (
        <div className="nav:hidden fixed top-0 left-0 right-0 z-30 bg-cream" style={{ height: TOPBAR_HEIGHT }} data-testid="mobile-topbar">
          {showSectionHeader ? (
            <MobileSectionHeader titleKey={sectionKey} right={<NotificationBell />} />
          ) : (
            /* `justify-between`, which is the whole change: the mark was alone
               in this row and the bell now takes the end of it. */
            <div className="flex h-full items-center justify-between px-3 border-b border-sand">
              <Logo size={24} />
              <NotificationBell />
            </div>
          )}
        </div>
      )}
      {isAdmin && mobileNav && (
        <div className="nav:hidden fixed inset-x-0 bottom-0 bg-worker text-cream z-30 p-4 overflow-y-auto" style={{ top: TOPBAR_HEIGHT }} data-testid="mobile-dash-drawer">
          {nav.map((item) => {
            const Icon = item.icon;
            const cls = "flex items-center gap-3 px-3 py-3 rounded-sm font-display text-base text-cream/90 hover:bg-cream/5 w-full text-left";
            if (item.action === "guide") {
              return (
                <button key={item.key} onClick={() => { setMobileNav(false); setGuideOpen(true); }} className={cls}>
                  <Icon size={18} /> {item.label}
                </button>
              );
            }
            return (
              <Link key={item.key} to={item.to} onClick={() => setMobileNav(false)} className={cls}>
                <Icon size={18} /> <span className="flex-1 truncate">{item.label}</span>
                {item.badge > 0 && (
                  <span
                    className="ml-auto min-w-5 px-1.5 h-5 rounded-full bg-hive text-worker text-xs font-display font-bold flex items-center justify-center"
                    data-testid={`mobile-${item.key}-badge`}
                  >
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-3 rounded-sm font-display text-base text-cream/90">
            <LogOut size={18} /> {t("dashLayout.logout")}
          </button>
        </div>
      )}

      {/* `min-w-0` is the first of T13-D0's two links, and it is the one every
          page inherits. A flex item's automatic minimum size is `auto`, which
          means it cannot be laid out narrower than its own content: with a
          `truncate` title inside, this `main` refused to go below 462px on a
          360px phone, opened the layout viewport, and cut every page in the
          dashboard off at the right — along with the fixed tab bar, whose
          `left:0;right:0` then resolved against 463px and pushed Menú off the
          screen entirely. Above 992px nothing moves: the sidebar is a fixed
          256px and this still takes the rest. */}
      <main className="dash-main flex-1 min-w-0 nav:ml-0">
        {/* Desktop top bar */}
        <div className="hidden nav:flex items-center justify-end px-6 lg:px-10 pt-6 gap-2 text-worker -mb-4">
          <LanguageToggle />
          <NotificationBell />
        </div>
        <div className="p-4 sm:p-6 lg:p-10 max-w-6xl">
          {children}

        </div>
      </main>

      <GuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}
